import {createSharedComposable} from '@vueuse/core';
import {useGtm} from '@gtm-support/vue-gtm';
import * as Sentry from '@sentry/nuxt';
import {storeToRefs} from 'pinia';
import {useShoppingListsStore} from '~/stores/shoppingLists';
import {useCustomerProfileStore} from '~/stores/customerProfile';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {
	getGuestMergeState,
	resetGuestMergeStateIfNewGuestData,
	setGuestMergeState,
	shouldShowMergeDialog,
} from '~/services/shoppingLists/storage';
import {
	enrichGuestItems,
	fetchProductsByVanrs,
	getProductSnapshotCacheKey,
} from '~/services/shoppingLists/guestProductSnapshot';
import type {
	ShoppingListMergeTargetOption,
	ShoppingListPreviewItem,
} from '~/types/shoppingListPreview';
import type {
	GuestProductSnapshot,
	GuestShoppingList,
	GuestShoppingListItem,
} from '~/services/shoppingLists/types';
import type {Gsoa_ShoppingList_GetItemsCollection as ShoppingListItem} from '~/openapi';
import {GLOBUS_HOME_GSOA_ID} from '#shared/constants';
import {appendShoppingListDateSuffix} from '~/utils/shoppingList';

const MAX_AMOUNT = 99;
const ITEM_CONCURRENCY = 4;
const RETRY_DELAYS = [300, 600, 1200];

function sleep(ms: number) {
	return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
	for (let attempt = 0; ; attempt++) {
		try {
			return await fn();
		} catch (error) {
			if (attempt >= RETRY_DELAYS.length) {
				throw error;
			}
			await sleep(RETRY_DELAYS[attempt]);
		}
	}
}

async function processBatch<T>(items: T[], worker: (item: T) => Promise<void>) {
	let index = 0;
	async function run() {
		while (index < items.length) {
			const current = items[index++];
			await worker(current);
		}
	}
	await Promise.all(Array.from({length: Math.min(ITEM_CONCURRENCY, items.length)}, () => run()));
}

function toPreviewItems(items: ShoppingListItem[] | GuestShoppingListItem[]): ShoppingListPreviewItem[] {
	return items.map((item) => ({
		Text: item.Text,
		VANR: item.VANR,
		GeneralizedProduct: 'GeneralizedProduct' in item ? item.GeneralizedProduct : undefined,
		productDetail: 'productDetail' in item ? item.productDetail : undefined,
	}));
}

function applySnapshotsToGuestItems(
	items: GuestShoppingListItem[],
	cache: Map<string, GuestProductSnapshot>,
	houseNumber: number,
): ShoppingListPreviewItem[] {
	return items.map((item) => {
		if (!item.VANR) {
			return {
				Text: item.Text,
				VANR: item.VANR,
				GeneralizedProduct: item.GeneralizedProduct,
			};
		}
		const snapshot = cache.get(getProductSnapshotCacheKey(houseNumber, item.VANR));
		return {
			Text: item.Text,
			VANR: item.VANR,
			GeneralizedProduct: item.GeneralizedProduct,
			productDetail: snapshot,
		};
	});
}

function useGuestShoppingListMergeImpl() {
	const shoppingListsStore = useShoppingListsStore();
	const customerProfileStore = useCustomerProfileStore();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {userIsLogged} = storeToRefs(customerProfileStore);
	const {selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const {productDetailsByVanrAndHouse} = storeToRefs(shoppingListsStore);
	const gtm = useGtm();

	const isOpen = ref(false);
	const isMerging = ref(false);
	const isLoadingTargets = ref(false);
	const error = ref<string | null>(null);
	const progress = reactive({current: 0, total: 0});
	const targetListId = ref<string | undefined>(undefined);
	const targetListOptions = ref<ShoppingListMergeTargetOption[]>([]);
	const guestPreviewItems = ref<ShoppingListPreviewItem[]>([]);

	const guestState = computed(() => shoppingListsStore.getGuestListsState());
	const guestList = computed(() => guestState.value.lists[0] ?? null);
	const itemsCount = computed(() => guestList.value?.items.length ?? 0);

	const houseNumber = computed(() => {
		const gsoaId = selectedHypermarketInstance.value?.content.gsoaId;
		return gsoaId ? Number(gsoaId) : GLOBUS_HOME_GSOA_ID;
	});

	const remote = shoppingListsStore.remoteRepository;

	async function enrichPreviewItems(items: ShoppingListItem[]) {
		const cache = productDetailsByVanrAndHouse.value;
		await enrichGuestItems(items, houseNumber.value, {cache});
	}

	async function loadMergeTargets() {
		if (!guestList.value) {
			return;
		}

		isLoadingTargets.value = true;
		error.value = null;

		try {
			if (guestState.value.lists.length > 1) {
				Sentry.captureMessage('Multiple guest shopping lists found during merge', {
					level: 'warning',
					extra: {count: guestState.value.lists.length},
				});
			}

			const cache = productDetailsByVanrAndHouse.value;
			const guestVanrs = guestList.value.items
				.map((item) => item.VANR)
				.filter((vanr): vanr is string => !!vanr);

			if (guestVanrs.length) {
				await fetchProductsByVanrs([...new Set(guestVanrs)], houseNumber.value, cache);
			}
			guestPreviewItems.value = applySnapshotsToGuestItems(
				guestList.value.items,
				cache,
				houseNumber.value,
			);

			const remoteLists = await withRetry(() => remote.fetchLists(true));
			const listEntries = await withRetry(() => remote.fetchItems(undefined, houseNumber.value));
			const allRemoteItems = listEntries.flatMap((entry) => entry.products.Items ?? []);
			await enrichPreviewItems(allRemoteItems);

			const itemsByListId = new Map(
				listEntries.map((entry) => [entry.id.toString(), entry.products.Items ?? []]),
			);

			targetListOptions.value = (remoteLists?.Items ?? []).map((list) => ({
				value: list.ID.toString(),
				label: list.Name,
				items: toPreviewItems(itemsByListId.get(list.ID.toString()) ?? []),
			}));

			const preselected = remoteLists?.Items?.find((list) => list.IsSelected)
				?? remoteLists?.Items?.[0];
			targetListId.value = preselected ? preselected.ID.toString() : undefined;
		} catch (e) {
			error.value = 'Nepodařilo se načíst vaše seznamy. Zkuste to prosím znovu.';
			Sentry.captureException(e, {tags: {feature: 'guest-shopping-list-merge'}});
		} finally {
			isLoadingTargets.value = false;
		}
	}

	async function mergeGuestList(guestListToMerge: GuestShoppingList, targetId: string) {
		const targetItemsEntry = await withRetry(() => remote.fetchItems(targetId, houseNumber.value));
		const targetItems = targetItemsEntry[0]?.products.Items ?? [];

		const itemsToMerge = [...guestListToMerge.items];
		await processBatch(itemsToMerge, async (guestItem: GuestShoppingListItem) => {
			const existingItem = guestItem.VANR
				? targetItems.find((item) => item.VANR === guestItem.VANR)
				: undefined;

			if (existingItem) {
				const mergedAmount = Math.min(
					MAX_AMOUNT,
					Math.max(existingItem.Amount ?? 1, guestItem.Amount ?? 1),
				);
				await withRetry(() => remote.updateItem(targetId, existingItem.ID.toString(), {
					Text: existingItem.Text,
					VANR: existingItem.VANR ?? undefined,
					Amount: mergedAmount,
					Purchased: existingItem.Purchased || guestItem.Purchased,
				}));
			} else {
				const response = await withRetry(() => remote.createItem(targetId, {
					Text: guestItem.Text,
					VANR: guestItem.VANR ?? undefined,
					Amount: guestItem.Amount ?? 1,
					Purchased: guestItem.Purchased,
					GeneralizedProduct: guestItem.GeneralizedProduct,
				}));
				if (response?.ID) {
					shoppingListsStore.recordItemCreatedAt(
						targetId,
						response.ID,
						guestItem.CreatedAt ?? guestItem.LastUpdate,
					);
				}
			}

			shoppingListsStore.removeGuestItem(guestListToMerge.ID, guestItem.ID);
			progress.current += 1;
		});

		shoppingListsStore.removeGuestList(guestListToMerge.ID);
	}

	function uniqueListName(baseName: string, existingNames: string[]): string {
		if (!existingNames.includes(baseName)) {
			return baseName;
		}
		let suffix = 2;
		while (existingNames.includes(`${baseName} (${suffix})`)) {
			suffix += 1;
		}
		return `${baseName} (${suffix})`;
	}

	function buildSaveAsNewListName(guestName: string, existingNames: string[]): string {
		return uniqueListName(appendShoppingListDateSuffix(guestName), existingNames);
	}

	async function finishMerge(action: 'merge' | 'save_new', targetId: string) {
		shoppingListsStore.clearGuestStorage();
		setGuestMergeState('done');

		shoppingListsStore.reset();
		await shoppingListsStore.fetchShoppingLists();

		if (action === 'save_new') {
			const targetList = shoppingListsStore.shoppingLists?.Items
				?.find((list) => list.ID === Number(targetId));
			if (targetList) {
				await shoppingListsStore.setSelectedShoppingList(targetId, targetList.Name);
			}
		}

		await shoppingListsStore.fetchShoppingListItems(targetId);

		const mergedItemsCount = progress.total;

		gtm?.push({
			event: 'shopping_list_merge',
			action,
			target_list_id: targetId,
			items_count: mergedItemsCount,
		});

		Sentry.addBreadcrumb({
			category: 'shopping-list',
			message: 'Guest shopping list merged',
			level: 'info',
			data: {action, targetId, items: mergedItemsCount},
		});

		isOpen.value = false;
	}

	async function runMergeAction(action: 'merge' | 'save_new', resolveTargetId: () => Promise<string>) {
		if (isMerging.value || !guestList.value) {
			return;
		}

		isMerging.value = true;
		error.value = null;
		progress.current = 0;
		progress.total = itemsCount.value;

		try {
			const targetId = await resolveTargetId();
			await mergeGuestList(guestList.value, targetId);
			await finishMerge(action, targetId);
		} catch (e) {
			error.value = 'Sloučení se nezdařilo. Zkuste to prosím znovu.';
			Sentry.captureException(e, {tags: {feature: 'guest-shopping-list-merge'}});
		} finally {
			isMerging.value = false;
		}
	}

	async function merge() {
		if (!targetListId.value) {
			error.value = 'Vyberte prosím cílový seznam.';
			return;
		}
		const selectedTargetId = targetListId.value;
		await runMergeAction('merge', () => Promise.resolve(selectedTargetId));
	}

	async function saveAsNew() {
		if (!guestList.value) {
			return;
		}
		const guestListSnapshot = guestList.value;
		await runMergeAction('save_new', async () => {
			const remoteLists = await withRetry(() => remote.fetchLists(true));
			const existingNames = remoteLists?.Items?.map((list) => list.Name) ?? [];
			const name = buildSaveAsNewListName(guestListSnapshot.Name, existingNames);
			const newList = await withRetry(() => remote.createList(name));
			if (!newList?.ID) {
				throw new Error('MERGE_TARGET_LIST_MISSING');
			}
			return newList.ID.toString();
		});
	}

	function discard() {
		shoppingListsStore.clearGuestStorage();
		setGuestMergeState('done');
		isOpen.value = false;
	}

	function dismiss() {
		setGuestMergeState('dismissed');
		isOpen.value = false;
	}

	function openIfNeeded() {
		if (!userIsLogged.value || itemsCount.value === 0) {
			return;
		}
		if (shouldShowMergeDialog(guestState.value)) {
			isOpen.value = true;
		}
	}

	watch(isOpen, (open) => {
		if (open) {
			loadMergeTargets();
		}
	});

	watch(() => guestState.value.lastModified, () => {
		if (userIsLogged.value) {
			return;
		}
		resetGuestMergeStateIfNewGuestData(guestState.value);
	});

	watch(userIsLogged, (logged, wasLogged) => {
		if (logged && !wasLogged && getGuestMergeState() !== 'done') {
			openIfNeeded();
		}
	}, {immediate: true});

	return {
		isOpen,
		isMerging,
		isLoadingTargets,
		error,
		progress,
		guestList,
		guestPreviewItems,
		targetListId,
		targetListOptions,
		itemsCount,
		merge,
		saveAsNew,
		discard,
		dismiss,
		openIfNeeded,
	};
}

const useGuestShoppingListMerge = import.meta.client
	? createSharedComposable(useGuestShoppingListMergeImpl)
	: useGuestShoppingListMergeImpl;

export default useGuestShoppingListMerge;
